import type WaDataGrid from "@web.awesome.me/webawesome-pro/dist/components/data-grid/data-grid.js";
import type WaDialog from "@web.awesome.me/webawesome-pro/dist/components/dialog/dialog.js";
import type WaPage from "@web.awesome.me/webawesome-pro/dist/components/page/page.js";
import htmx from "htmx.org";
import { html } from "lit";
import { checkNotNil } from "../../check-not-nil.js";
import { JsonBootstrapper } from "../../handlers/json-bootstrapper.js";
import { stateProperty } from "../../property.js";
import { Datetime } from "../../schemas/datetime.js";
import type { ParserSchema, ParserType } from "../../schemas/parser.js";
import { Tasks as Model } from "../../schemas/task.js";
import { gridSearch } from "../../search.js";
import { WmElement } from "../element.js";
import type { WmDeletionRequest } from "./details.js";

type Tasks = ParserType<typeof Tasks>;
const Tasks = stateProperty(Model.schema, { defaultValue: [] });

/**
A component that wraps a WaDataGrid and exposes a simple "tasks" property
to load content.
*/
export class WmAvailableTasksGrid extends WmElement {
  static readonly TAG_NAME = "wm-available-tasks-grid";

  static properties = {
    tasks: Tasks,
    isMobile: { attribute: false },
  };

  declare tasks: Tasks;
  declare isMobile: boolean;
  #pageObserver?: MutationObserver;
  #jsonBootstrapper: JsonBootstrapper<ParserSchema<typeof Model>>;

  constructor() {
    super();
    this.#jsonBootstrapper = new JsonBootstrapper({
      schema: Model.schema,
      callback: (v) => (this.tasks = v),
      logger: this.logger,
    });
  }

  connectedCallback() {
    super.connectedCallback();
    this.#initializePageObserver();
  }

  disconnectedCallback() {
    this.#pageObserver?.disconnect();
    this.#pageObserver = undefined;
    super.disconnectedCallback();
  }

  protected render() {
    return html`
      <slot @slotchange=${(e: Event) => this.#jsonBootstrapper.slotChanged(e)}></slot>
      ${this.#renderGrid()}`;
  }

  protected updated(): void {
    this.#handleUpdated();
  }

  #renderGrid() {
    return html`
      <wa-dialog label="Are you sure?" id="deletion-dialog">
        This action is irreversible.
        <div slot="footer" class="wa-cluster">
          <wa-button variant="brand" data-dialog="close">Cancel</wa-button>
          <wa-button id="deletion-confirmation" variant="danger" @click=${this.#confirmDeletion}>Confirm</wa-button>
        </div>
      </wa-dialog>
      <wa-data-grid
        label="Available tasks"
        row-key="id"
        with-search
        resizable
        striped
        paginage
        size="s"
       page-size="10"
      >
        <span slot="empty">You currently have no available tasks. Well done, monster.</span>
      </wa-data-grid>
    `;
  }

  async #handleUpdated() {
    const dataGrid = this.#queryGrid();
    await dataGrid.updateComplete;
    dataGrid.searchFn = gridSearch;
    const dateFormatter = (v: unknown) => {
      if (v == null) {
        return "";
      }
      return Datetime.formatPretty(Datetime.codec.parse(v));
    };
    dataGrid.columns = [
      {
        field: "description",
        label: "Description",
        formatter: (v, row) =>
          html`<a href="/app/tasks/${row.id}" @click=${this.#descriptionClickHandler(row.id as string)}>${v}</a>`,
      },
      {
        field: "createdAt",
        label: "Created At",
        hidden: this.isMobile,
        formatter: dateFormatter,
      },
      {
        field: "dueBy",
        label: "Due By",
        hidden: this.isMobile,
        formatter: dateFormatter,
      },
      {
        field: "tags",
        label: "Tags",
        hidden: this.isMobile,
        formatter: (v) => html`<wm-tag-cell .tags=${v}></wm-tag-cell>`,
      },
      {
        id: "actions",
        label: "Actions",
        hidden: this.isMobile,
        formatter: (_, row) => html`
          <wa-button size="xs" appearance="plain" @click=${() => htmx.ajax("POST", `/app/tasks/${row.id}/done`)}>
          <wa-icon name="check" style="color: var(--wa-color-success)"></wa-icon>
          </wa-button>
          <wm-delete-button size="xs" as-icon @click=${() => this.#promptDeletionConfirmation(row.id as string)}></wm-delete-button>
        `,
      },
    ];
    dataGrid.rowDetail = (row) =>
      html`<wm-task-details .details=${row} @wm-deletion-request=${(e: WmDeletionRequest) => this.#promptDeletionConfirmation(e.id)}></wm-task-details>`;
    dataGrid.data = this.tasks;
  }

  // TODO: deletion bullshit should be a higher level thing, since it's the same logic for every grid, and
  // there could be more than one grid on a page.
  #confirmDeletion() {
    const dialog = this.#queryDialog();
    const taskId = dialog.dataset.taskId;
    htmx.ajax("DELETE", `/app/tasks/${taskId}`);
  }

  #descriptionClickHandler(id: string) {
    return (e: PointerEvent) => {
      e.stopPropagation();
      e.preventDefault();
      htmx.ajax("GET", `/app/tasks/${id}`, {
        target: "body",
        push: "true",
      });
    };
  }

  #promptDeletionConfirmation(taskId: string) {
    const dialog = this.#queryDialog();
    dialog.dataset.taskId = taskId;
    dialog.open = true;
  }

  #initializePageObserver() {
    // At this time, we actually consider it an error if no wraping wa-page
    // can be found.
    const page = this.#queryPage();
    this.isMobile = page.view === "mobile";
    this.#pageObserver = new MutationObserver(() => {
      this.isMobile = page.view === "mobile";
    });
    this.#pageObserver.observe(page, {
      attributes: true,
      attributeFilter: ["view"],
    });
  }

  #queryGrid(): WaDataGrid {
    return this.queryNotNil("wa-data-grid");
  }

  #queryDialog(): WaDialog {
    return this.queryNotNil("wa-dialog");
  }

  #queryPage(): WaPage {
    return checkNotNil(document.querySelector("wa-page"));
  }
}

customElements.define(WmAvailableTasksGrid.TAG_NAME, WmAvailableTasksGrid);

declare global {
  interface HTMLElementTagNameMap {
    [WmAvailableTasksGrid.TAG_NAME]: WmAvailableTasksGrid;
  }
}
